import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, ArrowRight, Calculator, Check, Plus, Minus,
  Building2, Users, DollarSign, Shield, ChevronDown, 
  Sparkles, Download, Mail, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LogoFull } from '@/components/Logo';
import { api } from '@/services/api';
import { toast } from 'sonner';

const CATEGORY_LABELS = {
  liability: 'Liability', cyber: 'Cyber & Tech', property: 'Property',
  auto: 'Auto & Fleet', employee: 'Employee', benefits: 'Employee Benefits',
  executive: 'Executive', crime: 'Crime & Surety', specialty: 'Specialty', logistics: 'Logistics'
};

const CATEGORY_COLORS = {
  liability: 'bg-blue-50 text-blue-700',
  cyber: 'bg-violet-50 text-violet-700',
  property: 'bg-amber-50 text-amber-700',
  auto: 'bg-emerald-50 text-emerald-700',
  employee: 'bg-orange-50 text-orange-700',
  benefits: 'bg-rose-50 text-rose-700',
  executive: 'bg-indigo-50 text-indigo-700',
  crime: 'bg-red-50 text-red-700',
  specialty: 'bg-teal-50 text-teal-700',
  logistics: 'bg-cyan-50 text-cyan-700',
};

const INDUSTRIES = [
  { value: 'technology', label: 'Technology / SaaS' },
  { value: 'ecommerce', label: 'E-Commerce / Retail' },
  { value: 'construction', label: 'Construction' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'finance', label: 'Financial Services' },
  { value: 'food_service', label: 'Restaurant / Food' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'logistics', label: 'Logistics / Shipping' },
  { value: 'professional_services', label: 'Professional Services' },
  { value: 'nonprofit', label: 'Nonprofit' },
  { value: 'other', label: 'Other' },
];

const STEPS = ['business', 'coverages', 'results'];

const QuoteCalculator = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [quoteResult, setQuoteResult] = useState(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveEmail, setSaveEmail] = useState('');
  const [saveName, setSaveName] = useState('');
  const [saving, setSaving] = useState(false);

  // Form state
  const [businessName, setBusinessName] = useState('');
  const [industry, setIndustry] = useState('');
  const [employeeCount, setEmployeeCount] = useState(10);
  const [annualRevenue, setAnnualRevenue] = useState(1000000);
  const [selectedCoverages, setSelectedCoverages] = useState([]);
  const [expandedCategory, setExpandedCategory] = useState('');

  useEffect(() => {
    api.getCoverageBlocks().then(setBlocks).catch(console.error).finally(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => {
    const groups = {};
    blocks.forEach(b => {
      if (!groups[b.category]) groups[b.category] = [];
      groups[b.category].push(b);
    });
    return groups;
  }, [blocks]);

  const toggleCoverage = (type) => {
    setSelectedCoverages(prev => 
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  const handleCalculate = useCallback(async () => {
    if (selectedCoverages.length === 0) {
      toast.error('Select at least one coverage');
      return;
    }
    setCalculating(true);
    try {
      const result = await api.publicCalculateQuote({
        coverages: selectedCoverages,
        industry,
        employee_count: employeeCount,
        annual_revenue: annualRevenue,
        business_name: businessName
      });
      setQuoteResult(result);
      setStep(2);
    } catch (err) {
      toast.error('Failed to calculate quote');
    } finally {
      setCalculating(false);
    }
  }, [selectedCoverages, industry, employeeCount, annualRevenue, businessName]);

  const handleSaveLead = async (e) => {
    e.preventDefault();
    if (!saveEmail.trim()) return;
    setSaving(true);
    try {
      await api.savePublicLead(saveEmail, saveName, quoteResult?.quote_id);
      toast.success('Quote saved! Redirecting to sign up...');
      setShowSaveModal(false);
      setTimeout(() => navigate('/auth', { state: { fromQuote: quoteResult?.quote_id } }), 1500);
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const empMultiplier = employeeCount <= 10 ? 1.0 : employeeCount <= 50 ? 1.3 : employeeCount <= 200 ? 1.6 : 2.0;
  const revMultiplier = annualRevenue < 500000 ? 0.9 : annualRevenue < 2000000 ? 1.0 : annualRevenue < 10000000 ? 1.3 : 1.6;
  const quickEstimate = selectedCoverages.reduce((sum, type) => {
    const b = blocks.find(bl => bl.coverage_type === type);
    return sum + (b ? Math.round(b.base_premium * empMultiplier * revMultiplier) : 0);
  }, 0);

  if (loading) {
    return <div className="min-h-screen bg-[#F0F4F8] flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-[#1A3A64] border-t-transparent rounded-full" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#F0F4F8]" data-testid="quote-calculator-page">
      {/* Header */}
      <header className="bg-white border-b border-[#DCE4ED] sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#8DA0B9] hover:text-[#0D1B2A] transition-colors">
            <ArrowLeft size={18} /><span className="text-sm">Home</span>
          </Link>
          <LogoFull size="small" />
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Progress */}
        <div className="flex items-center gap-3 mb-8">
          {['Your Business', 'Select Coverages', 'Your Quote'].map((label, i) => (
            <React.Fragment key={i}>
              {i > 0 && <div className={`flex-1 h-0.5 ${i <= step ? 'bg-[#00AEEF]' : 'bg-[#DCE4ED]'}`} />}
              <button
                onClick={() => i < step && setStep(i)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                  i === step ? 'bg-[#1A3A64] text-white' : i < step ? 'bg-[#00AEEF]/10 text-[#1A3A64] cursor-pointer' : 'bg-[#E8EEF4] text-[#8DA0B9]'
                }`}
                data-testid={`step-${i}`}
              >
                {i < step ? <Check size={14} /> : <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs">{i + 1}</span>}
                <span className="hidden sm:inline">{label}</span>
              </button>
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Business Info */}
        <AnimatePresence mode="wait">
          {step === 0 && (
            <motion.div key="step0" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              <div className="bg-white rounded-2xl border border-[#DCE4ED] p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #1A3A64, #00AEEF)' }}>
                    <Building2 size={20} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-[#0D1B2A]" style={{ fontFamily: 'Outfit, sans-serif' }}>Tell Us About Your Business</h2>
                    <p className="text-sm text-[#8DA0B9]">We'll customize your quote based on your profile</p>
                  </div>
                </div>

                <div className="space-y-5">
                  <div>
                    <label className="text-sm font-medium text-[#3D5A80] mb-1.5 block">Business Name (optional)</label>
                    <Input value={businessName} onChange={e => setBusinessName(e.target.value)} placeholder="Acme Corp" className="border-[#DCE4ED]" data-testid="business-name-input" />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-[#3D5A80] mb-1.5 block">Industry</label>
                    <div className="relative">
                      <select value={industry} onChange={e => setIndustry(e.target.value)} className="w-full border border-[#DCE4ED] rounded-lg px-4 py-2.5 text-sm appearance-none bg-white" data-testid="industry-select">
                        <option value="">Select your industry...</option>
                        {INDUSTRIES.map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
                      </select>
                      <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8DA0B9] pointer-events-none" />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-[#3D5A80] mb-1.5 block">
                      Number of Employees: <span className="text-[#1A3A64] font-bold">{employeeCount}</span>
                      <span className="text-xs text-[#8DA0B9] ml-2">({empMultiplier}x rate)</span>
                    </label>
                    <input type="range" min="1" max="500" value={employeeCount} onChange={e => setEmployeeCount(parseInt(e.target.value))}
                      className="w-full h-2 bg-[#E8EEF4] rounded-lg appearance-none cursor-pointer accent-[#1A3A64]" data-testid="employee-slider" />
                    <div className="flex justify-between text-xs text-[#8DA0B9] mt-1">
                      <span>1</span><span>50</span><span>200</span><span>500</span>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-[#3D5A80] mb-1.5 block">
                      Annual Revenue: <span className="text-[#1A3A64] font-bold">${annualRevenue.toLocaleString()}</span>
                      <span className="text-xs text-[#8DA0B9] ml-2">({revMultiplier}x rate)</span>
                    </label>
                    <input type="range" min="100000" max="50000000" step="100000" value={annualRevenue} onChange={e => setAnnualRevenue(parseInt(e.target.value))}
                      className="w-full h-2 bg-[#E8EEF4] rounded-lg appearance-none cursor-pointer accent-[#00AEEF]" data-testid="revenue-slider" />
                    <div className="flex justify-between text-xs text-[#8DA0B9] mt-1">
                      <span>$100K</span><span>$2M</span><span>$10M</span><span>$50M</span>
                    </div>
                  </div>
                </div>

                <div className="mt-8 flex justify-end">
                  <Button className="btn-gradient px-6" onClick={() => setStep(1)} data-testid="next-to-coverages-btn">
                    Select Coverages <ArrowRight size={16} className="ml-2" />
                  </Button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 2: Coverage Selection */}
          {step === 1 && (
            <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              <div className="bg-white rounded-2xl border border-[#DCE4ED] p-6 sm:p-8">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #00AEEF, #00C4AD)' }}>
                      <Shield size={20} className="text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-[#0D1B2A]" style={{ fontFamily: 'Outfit, sans-serif' }}>Choose Your Coverage</h2>
                      <p className="text-sm text-[#8DA0B9]">{selectedCoverages.length} selected &bull; Est. ${quickEstimate}/mo</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  {Object.entries(grouped).map(([category, catBlocks]) => {
                    const selectedInCat = catBlocks.filter(b => selectedCoverages.includes(b.coverage_type)).length;
                    const isExpanded = expandedCategory === category;
                    const colors = CATEGORY_COLORS[category] || 'bg-gray-50 text-gray-700';

                    return (
                      <div key={category} className="border border-[#DCE4ED] rounded-xl overflow-hidden">
                        <button
                          onClick={() => setExpandedCategory(isExpanded ? '' : category)}
                          className="w-full p-4 flex items-center justify-between hover:bg-[#F0F4F8]/50 transition-colors"
                          data-testid={`category-${category}`}
                        >
                          <div className="flex items-center gap-3">
                            <span className={`px-2.5 py-1 rounded-lg text-xs font-bold ${colors}`}>
                              {CATEGORY_LABELS[category]}
                            </span>
                            <span className="text-sm text-[#8DA0B9]">{catBlocks.length} products</span>
                            {selectedInCat > 0 && (
                              <span className="px-2 py-0.5 bg-[#00AEEF]/10 text-[#1A3A64] rounded-full text-xs font-bold">
                                {selectedInCat} selected
                              </span>
                            )}
                          </div>
                          <ChevronDown size={16} className={`text-[#8DA0B9] transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        </button>

                        <AnimatePresence>
                          {isExpanded && (
                            <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                              <div className="px-4 pb-4 space-y-2">
                                {catBlocks.map(block => {
                                  const isSelected = selectedCoverages.includes(block.coverage_type);
                                  return (
                                    <button
                                      key={block.coverage_type}
                                      onClick={() => toggleCoverage(block.coverage_type)}
                                      className={`w-full text-left p-3 rounded-lg border transition-all flex items-start gap-3 ${
                                        isSelected ? 'border-[#00AEEF] bg-[#00AEEF]/5' : 'border-[#DCE4ED] hover:border-[#00AEEF]/40'
                                      }`}
                                      data-testid={`coverage-${block.coverage_type}`}
                                    >
                                      <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5 ${isSelected ? 'bg-[#00AEEF]' : 'border-2 border-[#DCE4ED]'}`}>
                                        {isSelected && <Check size={12} className="text-white" />}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between">
                                          <p className="text-sm font-medium text-[#0D1B2A]">{block.name}</p>
                                          <p className="text-sm font-semibold text-[#1A3A64] ml-2 flex-shrink-0">
                                            ${Math.round(block.base_premium * empMultiplier * revMultiplier)}<span className="text-xs font-normal text-[#8DA0B9]">/mo</span>
                                          </p>
                                        </div>
                                        <p className="text-xs text-[#8DA0B9] mt-0.5 line-clamp-1">{block.description}</p>
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </div>

                {/* Sticky bottom bar */}
                <div className="mt-6 pt-4 border-t border-[#DCE4ED] flex items-center justify-between">
                  <Button variant="outline" onClick={() => setStep(0)} className="border-[#DCE4ED]" data-testid="back-to-business-btn">
                    <ArrowLeft size={16} className="mr-2" /> Back
                  </Button>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-xs text-[#8DA0B9]">Estimated Monthly</p>
                      <p className="text-xl font-bold text-[#1A3A64]">${quickEstimate}<span className="text-sm font-normal">/mo</span></p>
                    </div>
                    <Button className="btn-gradient px-6" onClick={handleCalculate} disabled={calculating || selectedCoverages.length === 0} data-testid="calculate-quote-btn">
                      {calculating ? <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> : <><Calculator size={16} className="mr-2" />Get Quote</>}
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 3: Results */}
          {step === 2 && quoteResult && (
            <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              {/* Hero quote */}
              <div className="bg-white rounded-2xl border border-[#DCE4ED] overflow-hidden mb-6">
                <div className="p-6 sm:p-8" style={{ background: 'linear-gradient(135deg, #1A3A64 0%, #00AEEF 100%)' }}>
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles size={18} className="text-[#83CF44]" />
                    <span className="text-sm text-white/70">Your Custom Insurance Quote</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                    <div>
                      <p className="text-sm text-white/60">Monthly Premium</p>
                      <p className="text-4xl font-bold text-white">${quoteResult.summary.monthly_premium.toLocaleString(undefined, {maximumFractionDigits: 0})}<span className="text-lg font-normal text-white/60">/mo</span></p>
                    </div>
                    <div>
                      <p className="text-sm text-white/60">Annual (save ${quoteResult.summary.savings_annual.toFixed(0)})</p>
                      <p className="text-4xl font-bold text-[#83CF44]">${quoteResult.summary.annual_premium.toLocaleString(undefined, {maximumFractionDigits: 0})}<span className="text-lg font-normal text-white/60">/yr</span></p>
                    </div>
                    <div>
                      <p className="text-sm text-white/60">Total Coverage</p>
                      <p className="text-4xl font-bold text-white">${(quoteResult.summary.total_coverage / 1000000).toFixed(1)}<span className="text-lg font-normal text-white/60">M</span></p>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center gap-2 text-xs text-white/50">
                    <span>{quoteResult.summary.coverage_count} coverages</span>
                    <span>&bull;</span>
                    <span>{quoteResult.employee_count} employees</span>
                    <span>&bull;</span>
                    <span>Valid for 30 days</span>
                    <span>&bull;</span>
                    <span>ID: {quoteResult.quote_id}</span>
                  </div>
                </div>

                {/* Coverage breakdown */}
                <div className="p-6 sm:p-8">
                  <h3 className="font-semibold text-[#0D1B2A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Coverage Breakdown</h3>
                  <div className="space-y-2">
                    {quoteResult.coverages.map(cov => {
                      const colors = CATEGORY_COLORS[cov.category] || 'bg-gray-50 text-gray-700';
                      return (
                        <div key={cov.coverage_type} className="flex items-start gap-3 p-3 rounded-lg bg-[#F0F4F8]/50">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <p className="text-sm font-medium text-[#0D1B2A]">{cov.name}</p>
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${colors}`}>{CATEGORY_LABELS[cov.category]}</span>
                            </div>
                            <p className="text-xs text-[#8DA0B9]">{cov.description}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-sm font-semibold text-[#1A3A64]">${cov.adjusted_premium}/mo</p>
                            {cov.coverage_limit > 0 && <p className="text-xs text-[#8DA0B9]">${cov.coverage_limit.toLocaleString()} limit</p>}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-4 p-3 rounded-lg bg-[#E8EEF4] flex items-center justify-between text-sm">
                    <span className="text-[#3D5A80]">Rate multipliers applied:</span>
                    <span className="text-[#1A3A64] font-medium">
                      Employees: {quoteResult.multipliers.employee}x &bull; Revenue: {quoteResult.multipliers.revenue}x
                    </span>
                  </div>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Button className="btn-gradient py-6 text-lg" onClick={() => setShowSaveModal(true)} data-testid="save-build-btn">
                  <Mail size={20} className="mr-2" />
                  Save & Build Your Policy
                </Button>
                <Button variant="outline" className="py-6 text-lg border-[#DCE4ED]" onClick={() => { setStep(1); setQuoteResult(null); }} data-testid="adjust-quote-btn">
                  <ArrowLeft size={20} className="mr-2" />
                  Adjust Coverage
                </Button>
              </div>

              <p className="text-center text-xs text-[#8DA0B9] mt-4">
                This is an estimate. Final premiums are subject to underwriting. InsureLab is a technology provider, not an insurance company.
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Save Lead Modal */}
      <AnimatePresence>
        {showSaveModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-[#0D1B2A]/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowSaveModal(false)}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="bg-white rounded-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()} data-testid="save-lead-modal">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-[#0D1B2A]" style={{ fontFamily: 'Outfit, sans-serif' }}>Save Your Quote</h3>
                <button onClick={() => setShowSaveModal(false)} className="p-2 hover:bg-[#F0F4F8] rounded-lg"><X size={18} className="text-[#8DA0B9]" /></button>
              </div>
              <p className="text-sm text-[#3D5A80] mb-4">
                Enter your email to save this ${quoteResult?.summary?.monthly_premium?.toFixed(0)}/mo quote and start building your custom policy.
              </p>
              <form onSubmit={handleSaveLead} className="space-y-3">
                <Input value={saveName} onChange={e => setSaveName(e.target.value)} placeholder="Your name" className="border-[#DCE4ED]" data-testid="lead-name-input" />
                <Input type="email" value={saveEmail} onChange={e => setSaveEmail(e.target.value)} placeholder="your@email.com" className="border-[#DCE4ED]" required data-testid="lead-email-input" />
                <Button type="submit" className="w-full btn-gradient" disabled={saving} data-testid="save-lead-submit-btn">
                  {saving ? 'Saving...' : 'Save & Create Account'}
                  <ArrowRight size={16} className="ml-2" />
                </Button>
              </form>
              <p className="text-xs text-[#8DA0B9] text-center mt-3">
                By saving, you agree to our Terms of Service and Privacy Policy.
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default QuoteCalculator;
